import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, installments CTA close-on-click,
 * and the GLOWE BOX progress message.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** GLOWE BOX configuration */
  #gloweBoxLimit = 12;
  #gloweBoxPrice = 99900;
  #gloweBoxUnitPrice = 8325;

  /** @type {Set<number> | null} */
  #gloweEligibleVariantIds = null;

  /** @type {Promise<Set<number>> | null} */
  #gloweEligibleVariantsPromise = null;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (
      this.closest('theme-drawer')
    );
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartLinesUpdate
    );

    this.#themeDrawer?.addEventListener(
      DrawerOpenEvent.eventName,
      this.#handleDrawerOpen
    );

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }

    // Prepare the GLOWE eligible products and update the message.
    this.#updateGloweBoxMessage();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartLinesUpdate
    );

    this.#themeDrawer?.removeEventListener(
      DrawerOpenEvent.eventName,
      this.#handleDrawerOpen
    );
  }

  /**
   * Handles the theme-drawer opening — updates sticky state,
   * updates the GLOWE BOX message and wires up the installments CTA.
   */
  #handleDrawerOpen = () => {
    this.#updateStickyState();
    this.#updateGloweBoxMessage();

    // Close cart drawer when installments CTA is clicked to avoid overlapping dialogs.
    // Re-queried on every open so it survives cart content re-renders that
    // replace the shopify-payment-terms shadow root.
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector(
        '#shopify-installments-cta'
      );

      cta?.addEventListener(
        'click',
        () => this.#themeDrawer?.close(),
        { once: true }
      );
    });
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen =
      this.hasAttribute('auto-open') &&
      event.action === 'add' &&
      !this.#themeDrawer?.isOpen;

    // When the event originates inside an open MODAL <dialog> (e.g. quick-add),
    // defer the auto-open until that dialog's native `close` fires so its focus
    // restoration runs first — otherwise we'd capture the wrong
    // `#previouslyFocused`. Non-modal dialogs (e.g. the hotspot preview) don't
    // close on add and don't move focus, so `:modal` excludes them.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element
        ? event.target.closest('dialog:modal')
        : null
    );

    if (shouldAutoOpen && !sourceModal && !this.#isCartEmpty()) {
      this.#themeDrawer?.open();
    }

    event.promise
      ?.then(({ detail }) => {
        const settle = () =>
          requestAnimationFrame(() => {
            this.#updateStickyState();
            this.#updateGloweBoxMessage();
          });

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) {
            this.#themeDrawer?.open();
          }

          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener(
            'close',
            openAndSettle,
            { once: true }
          );
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn(
            '[cart-drawer] Event promise rejected:',
            error
          );
        }
      });

    // Update immediately so the message responds as quickly as possible.
    this.#updateGloweBoxMessage();
  };

  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

  #updateStickyState() {
    const dialog = this.#dialog;

    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., cart-items-component), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;

    dialog.setAttribute(
      'cart-summary-sticky',
      ratio > this.#summaryThreshold ? 'false' : 'true'
    );
  }

  /**
   * Loads all variant IDs belonging to the GLOWE eligible collection.
   *
   * This allows the cart drawer to count only eligible GLOWE accessories,
   * without depending on product tags or changing the existing products.
   *
   * @returns {Promise<Set<number>>}
   */
  #getGloweEligibleVariantIds() {
    if (this.#gloweEligibleVariantIds) {
      return Promise.resolve(this.#gloweEligibleVariantIds);
    }

    if (this.#gloweEligibleVariantsPromise) {
      return this.#gloweEligibleVariantsPromise;
    }

    this.#gloweEligibleVariantsPromise = fetch(
      `${window.Shopify?.routes?.root || '/'}collections/elegibles-de-glowe/products.json?limit=250`,
      {
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
        },
      }
    )
      .then((response) => {
        if (!response.ok) {
          throw new Error(
            `Unable to load GLOWE eligible products: ${response.status}`
          );
        }

        return response.json();
      })
      .then((data) => {
        const variantIds = new Set();

        for (const product of data.products || []) {
          for (const variant of product.variants || []) {
            if (variant.id) {
              variantIds.add(Number(variant.id));
            }
          }
        }

        this.#gloweEligibleVariantIds = variantIds;

        return variantIds;
      })
      .catch((error) => {
        console.warn(
          '[cart-drawer] Could not load GLOWE eligible products:',
          error
        );

        // Return an empty set instead of breaking the cart.
        return new Set();
      });

    return this.#gloweEligibleVariantsPromise;
  }

  /**
   * Gets the current cart and counts only products belonging to
   * the GLOWE eligible collection.
   *
   * @returns {Promise<number>}
   */
  async #getGloweEligibleQuantity() {
    try {
      const [eligibleVariantIds, cartResponse] = await Promise.all([
        this.#getGloweEligibleVariantIds(),
        fetch(
          `${window.Shopify?.routes?.root || '/'}cart.js`,
          {
            credentials: 'same-origin',
            headers: {
              Accept: 'application/json',
            },
          }
        ),
      ]);

      if (!cartResponse.ok) {
        throw new Error(
          `Unable to load cart: ${cartResponse.status}`
        );
      }

      const cart = await cartResponse.json();

      let quantity = 0;

      for (const item of cart.items || []) {
        const variantId = Number(item.variant_id);

        if (eligibleVariantIds.has(variantId)) {
          quantity += Number(item.quantity) || 0;
        }
      }

      return quantity;
    } catch (error) {
      console.warn(
        '[cart-drawer] Could not calculate GLOWE BOX quantity:',
        error
      );

      return 0;
    }
  }

  /**
   * Creates or returns the GLOWE BOX message element.
   *
   * @returns {HTMLElement | null}
   */
  #getOrCreateGloweBoxMessage() {
    const dialog = this.#dialog;

    if (!dialog) return null;

    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!summary) return null;

    let message = summary.querySelector('.glowe-cart-offer');

    if (message) return message;

    message = document.createElement('div');
    message.className = 'glowe-cart-offer';
    message.setAttribute('aria-live', 'polite');
    message.hidden = true;

    if (!document.head.querySelector('#glowe-cart-offer-styles')) {
      const style = document.createElement('style');

      style.id = 'glowe-cart-offer-styles';

      style.textContent = `
        .glowe-cart-offer {
          width: 100%;
          box-sizing: border-box;
          margin: 0 0 16px;
          padding: 13px 16px;
          background: #f6f6f6;
          color: inherit;
          text-align: center;
          font-size: 13px;
          line-height: 1.5;
          letter-spacing: 0.01em;
        }

        .glowe-cart-offer__main {
          display: block;
          font-size: 13px;
          line-height: 1.5;
        }

        .glowe-cart-offer__price {
          color: #c62828;
          font-weight: 700;
          white-space: nowrap;
        }

        .glowe-cart-offer__shipping {
          display: block;
          margin-top: 3px;
          font-size: 12px;
          font-weight: 600;
        }

        .glowe-cart-offer--complete {
          background: #f1f1f1;
        }

        .glowe-cart-offer--complete .glowe-cart-offer__main {
          font-weight: 600;
        }
      `;

      document.head.appendChild(style);
    }

    // Insert the message before the summary content.
    // This keeps it inside the native Horizon cart drawer.
    summary.insertBefore(message, summary.firstChild);

    return message;
  }

  /**
   * Updates the GLOWE BOX message according to the number
   * of eligible accessories currently in the cart.
   */
  async #updateGloweBoxMessage() {
    const dialog = this.#dialog;

    if (!dialog) return;

    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!summary) return;

    const quantity = await this.#getGloweEligibleQuantity();

    // The cart may have been re-rendered while the asynchronous
    // request was running. Query the current summary again.
    const currentDialog = this.#dialog;

    if (!currentDialog) return;

    const currentSummary = currentDialog.querySelector(
      '.cart-drawer__summary'
    );

    if (!currentSummary) return;

    let message = currentSummary.querySelector('.glowe-cart-offer');

    if (!message) {
      message = this.#getOrCreateGloweBoxMessage();
    }

    if (!message) return;

    // If there are no eligible products, there is no reason
    // to display the GLOWE BOX promotion.
    if (quantity <= 0) {
      message.hidden = true;
      message.textContent = '';
      return;
    }

    message.hidden = false;
    message.classList.remove('glowe-cart-offer--complete');

    if (quantity < this.#gloweBoxLimit) {
      const remaining = this.#gloweBoxLimit - quantity;

      message.innerHTML = `
        <span class="glowe-cart-offer__main">
          Agrega <strong>${remaining}</strong> más para llevar cada uno en
          <span class="glowe-cart-offer__price">$${this.#formatMoney(this.#gloweBoxUnitPrice)}</span>
        </span>
        <span class="glowe-cart-offer__shipping">¡Con envío gratis!</span>
      `;
    } else {
      message.classList.add('glowe-cart-offer--complete');

      message.innerHTML = `
        <span class="glowe-cart-offer__main">
          ¡Completaste tu GLOWE BOX!
          Llévala por
          <span class="glowe-cart-offer__price">$${this.#formatMoney(this.#gloweBoxPrice)}</span>
        </span>
        <span class="glowe-cart-offer__shipping">¡Con envío gratis!</span>
      `;
    }
  }

  /**
   * Formats COP values using the same simple visual format
   * used throughout the GLOWE storefront.
   *
   * @param {number} amount
   * @returns {string}
   */
  #formatMoney(amount) {
    return new Intl.NumberFormat('es-CO', {
      maximumFractionDigits: 0,
    })
      .format(amount)
      .replace(/\./g, '.');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}